import type { StrategyAction } from '../types/solver'

export interface PreflopSpotAction {
  action: StrategyAction
  range: string
}

export interface PreflopSpot {
  id: string
  label: string
  heroPosition: string
  villainPosition?: string
  actionHistory: string
  potBb: number
  effectiveStackBb: number
  actions: PreflopSpotAction[]
  rationale: string
}

const raise = (id: string, label: string, range: string, amountBb?: number): PreflopSpotAction => ({
  action: {
    id,
    kind: 'raise',
    label: label.replace('Open', 'オープン').replace('Raise', 'レイズ').replace('3-bet', '3ベット').replace('4-bet', '4ベット'),
    amountBb,
    frequency: 0,
  },
  range,
})

const call = (range: string): PreflopSpotAction => ({
  action: { id: 'call', kind: 'call', label: 'Call', frequency: 0 },
  range,
})

/**
 * Transparent educational baseline, not a preflop-solver output. Ranges assume
 * 100bb, 6-max, no ante and no rake so the trainer can cover preflop while the
 * bundled WASM engine remains postflop-only.
 */
export const PREFLOP_SPOTS: PreflopSpot[] = [
  {
    id: 'rfi-utg', label: 'UTG first in', heroPosition: 'UTG', actionHistory: '全員フォールドでUTGまで', potBb: 1.5, effectiveStackBb: 100,
    actions: [raise('open', 'Open 2.5bb', 'AA-22,AKs-A2s,AKo-AJo,KQs-K9s,KQo,QJs-Q9s,QJo:0.5,JTs-J9s,JTo:0.35,T9s-76s,65s:0.5', 2.5)],
    rationale: 'UTGは後ろに5人いるため、支配されにくいAx・ブロードウェイ・ポケットペアを中心に狭くオープンします。境界のオフスートはフォールド寄りです。',
  },
  {
    id: 'rfi-hj', label: 'HJ first in', heroPosition: 'HJ', actionHistory: 'UTGフォールド、HJまで', potBb: 1.5, effectiveStackBb: 100,
    actions: [raise('open', 'Open 2.5bb', 'AA-22,AKs-A2s,AKo-ATo,KQs-K8s,KQo-KJo,QJs-Q8s,QJo,JTs-J8s,JTo:0.5,T9s-65s,54s:0.5', 2.5)],
    rationale: 'HJはUTGより後ろの人数が1人少なく、スーテッドブロードウェイとコネクターを少し広げられます。弱いオフスートはまだ慎重です。',
  },
  {
    id: 'rfi-co', label: 'CO first in', heroPosition: 'CO', actionHistory: 'UTG・HJフォールド、COまで', potBb: 1.5, effectiveStackBb: 100,
    actions: [raise('open', 'Open 2.5bb', 'AA-22,AKs-A2s,AKo-A9o,KQs-K6s,KQo-KTo,QJs-Q7s,QJo-QTo,JTs-J7s,JTo,T9s-65s,T9o:0.5,98o:0.25,54s', 2.5)],
    rationale: 'COはポジション獲得の価値が高まり、スーテッドハンドと中程度のオフスートブロードウェイまで利益的にオープンしやすくなります。',
  },
  {
    id: 'rfi-btn', label: 'BTN first in', heroPosition: 'BTN', actionHistory: 'COまでフォールド、BTNまで', potBb: 1.5, effectiveStackBb: 100,
    actions: [raise('open', 'Open 2.5bb', 'AA-22,AKs-A2s,AKo-A2o,KQs-K2s,KQo-K7o,QJs-Q2s,QJo-Q8o,JTs-J4s,JTo-J8o,T9s-T5s,T9o-T8o,98s-54s,97s-53s,86s-64s,75s,64s,53s', 2.5)],
    rationale: 'BTNは常にポストフロップでポジションを持てるため最も広くオープンします。ブラインドのフォールドエクイティも大きな収益源です。',
  },
  {
    id: 'rfi-sb', label: 'SB first in', heroPosition: 'SB', villainPosition: 'BB', actionHistory: 'BTNまでフォールド、SBまで', potBb: 1.5, effectiveStackBb: 99.5,
    actions: [raise('open', 'Raise 3bb', 'AA-22,AKs-A2s,AKo-A2o,KQs-K2s,KQo-K7o,QJs-Q3s,QJo-Q8o,JTs-J5s,JTo-J8o,T9s-T5s,T9o-T8o,98s-54s,97s-53s,86s-64s,75s,64s,53s', 3)],
    rationale: 'SBは相手がBBだけなので広く参加できますが、コールされると常にOOPです。BTNより少し大きいサイズでBBのエクイティ実現を抑えます。',
  },
  {
    id: 'bb-vs-btn', label: 'BB vs BTN open', heroPosition: 'BB', villainPosition: 'BTN', actionHistory: 'BTNが2.5bbへオープン、SBフォールド', potBb: 4, effectiveStackBb: 97.5,
    actions: [
      raise('3bet', '3-bet 10bb', 'AA-TT,AKs-AQs,AKo,A5s-A4s,KQs:0.5,KJs:0.25,QJs:0.25,JTs:0.25,T9s:0.25', 10),
      call('99-22,AJs-A2s,AQo-A8o,KQs-K4s,KQo-K9o,QJs-Q6s,QJo-Q9o,JTs-J6s,JTo-J9o,T9s-T5s,T9o,98s-54s,97s-53s,86s-64s,75s,64s,53s'),
    ],
    rationale: 'BBはすでに1bb投資済みで最後に行動するため広くディフェンスできます。強い線形部分とブロッカーを持つAxsを3ベットへ回します。',
  },
  {
    id: 'bb-vs-co', label: 'BB vs CO open', heroPosition: 'BB', villainPosition: 'CO', actionHistory: 'COが2.5bbへオープン、BTN・SBフォールド', potBb: 4, effectiveStackBb: 97.5,
    actions: [
      raise('3bet', '3-bet 10bb', 'AA-JJ,AKs-AQs,AKo,A5s-A4s,KQs:0.35', 10),
      call('TT-22,AJs-A2s,AQo-A9o,KQs-K7s,KQo-KTo,QJs-Q8s,QJo-QTo,JTs-J8s,JTo,T9s-65s,98o:0.25,97s-64s,86s-75s,54s'),
    ],
    rationale: 'COのオープンはBTNより強いため、BBのコール・3ベット両レンジを少し絞ります。相手レンジに対して支配されやすいオフスートを減らします。',
  },
  {
    id: 'sb-vs-btn', label: 'SB vs BTN open', heroPosition: 'SB', villainPosition: 'BTN', actionHistory: 'BTNが2.5bbへオープン', potBb: 4, effectiveStackBb: 99.5,
    actions: [
      raise('3bet', '3-bet 11bb', 'AA-99,AKs-ATs,AKo-AQo,A5s-A2s,KQs-KJs,KQo,QJs,JTs,T9s', 11),
      call('88-22,A9s-A6s,KTs-K8s,QTs-Q8s,JTs-J8s,T9s-76s,AJo-ATo,KJo-KTo,QJo'),
    ],
    rationale: 'SBはコールするとOOPかつBBのスクイーズも受けるため、BBより3ベット中心です。コールはポストフロップ性能の高いハンドへ寄せます。',
  },
  {
    id: 'btn-vs-co', label: 'BTN vs CO open', heroPosition: 'BTN', villainPosition: 'CO', actionHistory: 'COが2.5bbへオープン', potBb: 4, effectiveStackBb: 100,
    actions: [
      raise('3bet', '3-bet 8bb', 'AA-QQ,AKs-AQs,AKo,A5s-A4s,KQs:0.5', 8),
      call('JJ-22,AJs-A2s,AQo-AJo,KQs-K9s,KQo,QJs-Q9s,QJo,JTs-J9s,T9s-76s,65s'),
    ],
    rationale: 'BTNはポジションを持つためコールでエクイティを実現しやすく、SBよりフラットを多く残せます。プレミアムと一部Axsを3ベットします。',
  },
  {
    id: 'btn-vs-bb-3bet', label: 'BTN vs BB 3-bet', heroPosition: 'BTN', villainPosition: 'BB', actionHistory: 'BTN 2.5bb → BB 10bb', potBb: 13, effectiveStackBb: 90,
    actions: [
      raise('4bet', '4-bet 22bb', 'AA-QQ,AKs,AKo,A5s:0.5', 22),
      call('JJ-22,AQs-AJs,AQo,KQs-KJs,QJs,JTs,T9s-76s,65s,A5s-A4s'),
    ],
    rationale: 'BTNはポジションを持つため、3ベットに対してコールを確保できます。4ベットは強いバリューとA5sのようなブロッカーブラフが中心です。',
  },
  {
    id: 'co-vs-btn-3bet', label: 'CO vs BTN 3-bet', heroPosition: 'CO', villainPosition: 'BTN', actionHistory: 'CO 2.5bb → BTN 8bb', potBb: 12, effectiveStackBb: 92,
    actions: [
      raise('4bet', '4-bet 21bb', 'AA-KK,AKs,AKo:0.5,A5s:0.35', 21),
      call('QQ-77,AQs-AJs,AQo,KQs,QJs,JTs,T9s,A5s-A4s'),
    ],
    rationale: 'COは3ベッターに対してOOPになるため、BTNで受ける場合よりディフェンスを絞ります。強いハンドを4ベットし、プレイアビリティの高い部分をコールします。',
  },
  {
    id: 'bb-vs-sb', label: 'BB vs SB raise', heroPosition: 'BB', villainPosition: 'SB', actionHistory: 'SBが3bbへレイズ', potBb: 4, effectiveStackBb: 97,
    actions: [
      raise('3bet', '3-bet 10bb', 'AA-99,AKs-ATs,AKo-AQo,A5s-A2s,KQs-KTs,KQo,QJs-JTs,QJo,J9s,T9s', 10),
      call('88-22,A9s-A2s,AJo-A2o,K9s-K2s,KJo-K7o,QTs-Q4s,QJo-Q8o,JTs-J5s,JTo-J8o,T9s-T5s,T9o-T8o,98s-54s,97s-53s,86s-64s,75s,64s,53s'),
    ],
    rationale: 'BBはポジションを持ち、すでに1bb入っているためSBの広いレイズへ非常に広く続行できます。3ベットもBTN対BBより線形に広がります。',
  },
]

export const PREFLOP_BASELINE_LABEL = '100bb 6-max・レーキなし学習用ベースライン v1'
